import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, OnInit, inject, input, output } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DistributionGroupsService } from '@clubhub/ignite/services/distribution-groups/distribution-groups.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MSS_FORMLY_IMPORTS } from '@shared/components/formly/formly-standalone-imports';
import { FormFieldType } from '@shared/components/formly/formly.config';
import { FormsValidators } from '@shared/components/forms/validators';
import { clientIds } from '@shared/constants';
import { paymentRouteNames } from '@shared/constants/payment-routes';
import { FindAddressResponse } from '@shared/models/address';
import { FirmDetails } from '@shared/models/sso-user-firm-details';
import { AgencyNumberService, AuthorizeService } from '@shared/services';
import { AddressLookupService } from '@shared/services/address-lookup.service';
import { ToastService } from '@shared/services/toast.service';
import { UserDetailsService } from '@shared/services/user-details.service';
import { firstValueFrom, map, tap } from 'rxjs';

@Component({
  selector: 'lib-firm-details',
  templateUrl: 'firm-details.component.html',
  imports: [FormsModule, ReactiveFormsModule, ...MSS_FORMLY_IMPORTS],
})
export class FirmDetailsComponent implements OnInit {
  private readonly authService = inject(AuthorizeService);
  private readonly userDetailsService = inject(UserDetailsService);
  private readonly addressLookupService = inject(AddressLookupService);
  private readonly agencyNumberService = inject(AgencyNumberService);
  private readonly toastService = inject(ToastService);
  private readonly distributionGroupsService = inject(DistributionGroupsService);

  public readonly showSave = input(true);
  public hasInitialised = false;
  public model: FirmDetails & { fcaNumber?: unknown } | undefined;
  public form = new UntypedFormGroup({});
  public fields: FormlyFieldConfig[] = [];
  public readonly requestForbidden = output();
  public readonly save = output<FirmDetails>();

  private isManualAddress = false;
  private addressSearchSuccessful = false;
  public addresses: FindAddressResponse[] = [];
  private agencyNumberFocused = false;
  private hasLAndGPaymentRoute = false;
  private hasDistributionGroups = false;

  public ngOnInit(): void {
    this.fetchFirmDetails();
    this.hasAccessToMCPaymentRoute();
    this.distributionGroupsService.getDistributionGroups().subscribe((distributionGroups) => {
      this.hasDistributionGroups = distributionGroups.length > 0;
    });
  }

  public get isValid(): boolean {
    return this.form.valid;
  }

  public hasAccessToMCPaymentRoute(): void {
    this.distributionGroupsService.getDistributionGroupsPaymentRoutes().subscribe(paymentRoutes => {
      this.hasLAndGPaymentRoute = paymentRoutes.find(x => x.name === paymentRouteNames.legalAndGeneral)?.value ?? false;
    });
  }

  public fetchFirmDetails(): void {
    this.userDetailsService.getFirmDetails().subscribe({
      next: details => this.initializeForm(details),
      error: (error: HttpErrorResponse) => {
        if ((error.status as HttpStatusCode) === HttpStatusCode.Forbidden) {
          this.requestForbidden.emit(undefined);
        } else {
          this.initializeForm();
        }
      },
    });
  }

  public initializeForm(model?: FirmDetails): void {
    const defaultProps = {};

    this.model = model ?? ({} as FirmDetails);
    // use session storage agency number if no firm details have been returned
    if (!model && this.authService.agencyNumber) {
      this.model.agencyNumber = this.authService.agencyNumber;
    }
    this.model.fcaNumber = this.authService.user?.frnNumber;
    this.form.patchValue(this.model);

    this.isManualAddress = !!model;

    this.fields = [
      ...(this.hideAgencyNumberField
        ? []
        : [
          {
            type: FormFieldType.Template,
            className: 'h2',
            props: {
              label: 'Firm details',
            },
            expressions: {
              hide: () => this.showSave(),
            },
          },
          {
            key: 'agencyNumber',
            type: FormFieldType.Number,
            props: {
              label: 'L&G agency number (optional)',
              text: 'L&G agency number (optional)',
              helpText: 'If you are a Mortgage Club Member please ensure you provide your correct agency number, as this will ensure your correct Proc fee amount is displayed in your ESIS',
              ...defaultProps,
              maxLength: 7,
              focus: () => (this.agencyNumberFocused = true),
              blur: () => (this.agencyNumberFocused = false),
            },
            expressions: {
              hide: () => !this.hasLAndGPaymentRoute && this.hasDistributionGroups,
            },
            validators: {
              validation: [FormsValidators.integer],
            },
            asyncValidators: [
              {
                expression: this.lookupFirmName.bind(this),
                message: 'This agency number is not valid. If you don\'t know your agency number please contact Agency Enquiries on 0370 900 5010.',
              },
            ],
          },
        ]),
      {
        key: 'firmName',
        type: FormFieldType.Input,
        props: {
          label: 'Firm name',
          text: 'Firm name',
          ...defaultProps,
          required: true,
        },
      },
      {
        key: 'postcode',
        type: FormFieldType.PostCode,
        props: {
          label: 'Postcode',
          text: 'Postcode',
          submitText: 'Find address',
          linkText: 'Enter address manually',
          ...defaultProps,
          required: true,
          setAddressType: this.toggleAddressSearchType.bind(this),
          clickHandler: this.searchAddress.bind(this),
        },
        expressions: {
          hide: () => this.isManualAddress,
        },
        validators: {
          validation: [FormsValidators.postcode],
        },
      },
      {
        key: 'addressLineLookUp',
        type: FormFieldType.Select,
        props: {
          label: 'Address',
          text: 'Address',
          placeholder: 'Please Select...',
          options: [],
          ...defaultProps,
          required: true,
          change: () => this.populateAddressFields(),
        },
        expressions: {
          hide: () => !this.addressSearchSuccessful || this.isManualAddress,
        },
      },
      {
        key: 'addressLine1',
        type: FormFieldType.Input,
        props: {
          label: 'Address line 1',
          text: 'Address line 1',
          ...defaultProps,
          required: true,
        },
        expressions: {
          hide: () => !this.isManualAddress,
        },
      },
      {
        key: 'addressLine2',
        type: FormFieldType.Input,
        props: {
          label: 'Address line 2',
          text: 'Address line 2',
          ...defaultProps,
        },
        expressions: {
          hide: () => !this.isManualAddress,
        },
      },
      {
        key: 'town',
        type: FormFieldType.Input,
        props: {
          label: 'Town',
          text: 'Town',
          ...defaultProps,
          required: true,
        },
        expressions: {
          hide: () => !this.isManualAddress,
        },
      },
      {
        key: 'county',
        type: FormFieldType.Input,
        props: {
          label: 'County',
          text: 'County',
          ...defaultProps,
          required: false,
        },
        expressions: {
          hide: () => !this.isManualAddress,
        },
      },
      {
        key: 'postcode',
        type: FormFieldType.Input,
        props: {
          label: 'Postcode',
          text: 'Postcode',
          linkText: 'Search for address',
          setAddressType: this.toggleAddressSearchType.bind(this),
          ...defaultProps,
          required: true,
        },
        expressions: {
          hide: () => !this.isManualAddress,
        },
      },
      {
        key: 'contact',
        type: FormFieldType.Input,
        props: {
          label: 'Contact (broker name)',
          text: 'Contact (broker name)',
          ...defaultProps,
          required: true,
        },
      },
      {
        key: 'telephone',
        type: FormFieldType.Input,
        props: {
          label: 'Telephone number',
          text: 'Telephone number',
          inputType: 'tel',
          maxLength: 15,
          ...defaultProps,
          required: true,
        },
        validators: {
          telephone: {
            expression: (fc: UntypedFormControl) => !FormsValidators.telephone(fc),
            message: 'Enter valid telephone number',
          },
        },
      },
      {
        key: 'fcaNumber',
        type: FormFieldType.Number,
        props: {
          label: 'FCA/FRN number',
          text: 'FCA/FRN number',
          ...defaultProps,
          disabled: true,
        },
      },
    ];

    this.hasInitialised = true;
  }

  public get hideAgencyNumberField(): boolean {
    // Hide Agency Number field from SimplyBiz SSO users
    return this.authService.isSsoUser && this.authService.ssoClientId === clientIds.simplyBiz;
  }

  public toggleAddressSearchType(): void {
    this.isManualAddress = !this.isManualAddress;
  }

  public populateAddressFields(): void {
    const addressId = this.form.value.addressLineLookUp as string | undefined;
    if (!addressId?.length) {
      return;
    }

    this.addressLookupService.retrieveAddress(addressId).subscribe(newAddress => {
      if (!this.model) {
        return;
      }

      const [firstLine, ...secondLine] = newAddress.lines;

      this.model.addressLine1 = firstLine.trim();
      this.model.addressLine2 = secondLine.join(', ').trim();
      this.model.town = newAddress.town ?? '';
      this.model.county = newAddress.county ?? '';
      this.model.postcode = newAddress.postcode ?? this.model.postcode;

      this.form.patchValue(this.model);
    });
  }

  public searchAddress(): void {
    if (!FormsValidators.postcodeRegex.test(this.form.value.postcode)) {
      return;
    }
    this.addressLookupService.findAddress(this.form.value.postcode).subscribe(addresses => {
      this.addressSearchSuccessful = addresses.length > 0;
      const addressFieldConfig: FormlyFieldConfig | undefined = this.fields.find(
        x => x.key === 'addressLineLookUp' && x.type === FormFieldType.Select,
      );

      if (addressFieldConfig?.props && this.addressSearchSuccessful) {
        addressFieldConfig.props.options = addresses.map(a => ({ label: a.text, value: a.id }));
        this.addresses = addresses;
      }
      this.form.get('postcode')?.updateValueAndValidity();
    });
  }

  public async lookupFirmName(control: AbstractControl): Promise<boolean> {
    if (control.value === null || control.value === undefined) {
      return true;
    }

    const { valid, result: firmName } = await firstValueFrom(this.agencyNumberService.lookupAgencyNumber(control.value));
    // Since validator runs whenever the value changes (including when the model is set), we only want it to update the
    // firm name field when the user actually triggered the change.
    if (valid && this.agencyNumberFocused) {
      this.form.patchValue({ firmName });
    }
    return valid;
  }

  public markAllAsTouched() {
    this.form.markAllAsTouched();
  }

  public saveFirmDetails(showToast = true): Promise<void> {
    if (!this.model) {
      return Promise.reject();
    }

    return this.form.valid
      ? firstValueFrom(
        this.userDetailsService.saveFirmDetails(this.model).pipe(
          tap(() => {
            this.authService.setAgencyNumber(this.model?.agencyNumber?.toString());
            if (showToast) {
              this.toastService.success('Successfully saved firm details');
            }
            this.save.emit(this.form.value);
          }),
          map(() => undefined),
        ),
      )
      : Promise.reject();
  }
}
